import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Shield, Eye, Lock, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { devError } from "../utils/devLogger";

export const ConsentBanner = ({ onConsented }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConsent = async (type) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/consent/grant`, { consent_type: type });
      onConsented(type);
    } catch (error) {
      devError("Consent error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="consent-banner">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-indigo-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-medium text-sky-900">AI Data Processing Consent</h2>
              <p className="text-xs text-slate-400">Required before questionnaire evaluation</p>
            </div>
          </div>

          {/* Main text */}
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            LumiFit uses AI to evaluate your questionnaire responses and generate personalized wellness plans.
            Your anonymized answers are processed by our AI partner (OpenAI) under a <strong>Zero Data Retention</strong> agreement —
            your data is never stored or used for AI training.
          </p>

          {/* Expandable details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-sm text-sky-500 hover:text-sky-700 mb-4 transition-colors"
            data-testid="consent-details-toggle"
          >
            <Eye className="w-4 h-4" />
            {showDetails ? "Hide details" : "What data is processed?"}
            {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showDetails && (
            <div className="bg-slate-50 rounded-xl p-4 mb-4 text-sm text-slate-600 space-y-2 animate-in fade-in duration-200">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p><strong>Sent:</strong> Your anonymized mood, energy, stress, sleep, and focus scores.</p>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p><strong>Not sent:</strong> Your email, name, or any personal identifiers.</p>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p><strong>Encryption:</strong> TLS 1.3 in transit, AES-256 at rest.</p>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p><strong>Retention:</strong> Zero Data Retention by OpenAI. Your data on our servers follows our published retention schedule.</p>
              </div>
              <a href="/privacy" target="_blank" className="text-sky-500 hover:underline text-xs block mt-2">
                Read full Privacy Policy
              </a>
            </div>
          )}

          {/* Consent buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => handleConsent("always")}
              disabled={submitting}
              className="w-full glow-button py-3"
              data-testid="consent-always-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              I Agree — Remember My Choice
            </Button>

            <Button
              onClick={() => handleConsent("once")}
              disabled={submitting}
              variant="outline"
              className="w-full py-3 text-sky-700 border-sky-200 hover:bg-sky-50"
              data-testid="consent-once-btn"
            >
              Agree for This Evaluation Only
            </Button>
          </div>

          <p className="text-xs text-slate-400 text-center mt-4">
            You can change your consent preference anytime in Settings.
            <br />
            Art. 9(2)(a) GDPR &middot; Art. 22 GDPR
          </p>
        </div>
      </div>
    </div>
  );
};

export const useConsentStatus = () => {
  const [hasConsent, setHasConsent] = useState(null); // null = loading
  const [consentType, setConsentType] = useState(null);

  const checkConsent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/consent/status`);
      setHasConsent(res.data.has_consent);
      setConsentType(res.data.consent_type);
    } catch (error) {
      setHasConsent(false);
    }
  }, []);

  useEffect(() => {
    checkConsent();
  }, [checkConsent]);

  const markUsed = useCallback(async () => {
    try {
      await axios.post(`${API}/consent/mark-used`);
    } catch (error) {
      devError("Mark used error:", error);
    }
  }, []);

  return { hasConsent, consentType, checkConsent, markUsed };
};
